import React from 'react';
import { BookingSummary, BookingStatus, PaymentStatus } from '../../types/booking';

interface BookingSummaryCardsProps {
  summary: BookingSummary;
  onCardClick?: (filter: any) => void;
}

const BookingSummaryCards: React.FC<BookingSummaryCardsProps> = ({
  summary,
  onCardClick
}) => {
  const cards = [
    {
      title: 'Total Bookings',
      value: summary.totalBookings,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      bgGradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({})
    },
    {
      title: 'Pending',
      value: summary.pendingBookings,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgGradient: 'from-yellow-500 to-yellow-600',
      iconBg: 'bg-yellow-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({ status: BookingStatus.PENDING })
    },
    {
      title: 'Confirmed',
      value: summary.confirmedBookings,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgGradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({ status: BookingStatus.CONFIRMED })
    },
    {
      title: 'In Progress',
      value: summary.inProgressBookings,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      bgGradient: 'from-indigo-500 to-indigo-600',
      iconBg: 'bg-indigo-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({ status: BookingStatus.IN_PROGRESS })
    },
    {
      title: 'Completed',
      value: summary.completedBookings,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      bgGradient: 'from-teal-500 to-teal-600',
      iconBg: 'bg-teal-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({ status: BookingStatus.COMPLETED })
    },
    {
      title: 'Cancelled',
      value: summary.cancelledBookings,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgGradient: 'from-red-500 to-red-600',
      iconBg: 'bg-red-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({ status: BookingStatus.CANCELLED })
    },
    {
      title: 'Total Revenue',
      value: `$${summary.totalRevenue.toLocaleString()}`,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgGradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({ paymentStatus: PaymentStatus.PAID })
    },
    {
      title: 'Pending Payments',
      value: summary.pendingPayments,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      bgGradient: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-400/20',
      textColor: 'text-white',
      onClick: () => onCardClick?.({ paymentStatus: PaymentStatus.PENDING })
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {cards.map((card, index) => (
        <div
          key={index}
          onClick={card.onClick}
          className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${card.bgGradient} p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 cursor-pointer group`}
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10 group-hover:scale-150 transition-transform duration-500"></div>
          
          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              <p className={`text-sm font-medium ${card.textColor} opacity-90 mb-2`}>
                {card.title}
              </p>
              <p className={`text-3xl font-bold ${card.textColor} tracking-tight`}>
                {card.value}
              </p>
            </div>
            <div className={`${card.iconBg} ${card.textColor} p-3 rounded-lg group-hover:scale-110 transition-transform duration-300`}>
              {card.icon}
            </div>
          </div>

          {/* Hover indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
        </div>
      ))}
    </div>
  );
};

export default BookingSummaryCards;
